import type { JSONContent } from '@tiptap/core';

// Convert Markdown string to TipTap JSON document
export function fromMarkdown(markdown: string): JSONContent {
  const lines = markdown.split('\n');
  const nodes: JSONContent[] = [];

  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Skip empty lines between blocks
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Admonition: !!! type "title" or ??? type "title"
    const admonitionMatch = line.match(/^([!?]{3})\s+(\S+)(?:\s+"([^"]*)")?/);
    if (admonitionMatch) {
      const collapsible = admonitionMatch[1] === '???';
      const type = admonitionMatch[2];
      const title = admonitionMatch[3] || '';
      const bodyLines: string[] = [];
      i++;
      while (i < lines.length && (lines[i].startsWith('    ') || lines[i].trim() === '')) {
        bodyLines.push(lines[i].startsWith('    ') ? lines[i].slice(4) : '');
        i++;
      }
      const bodyContent = bodyLines.join('\n').trim();
      const childDoc = fromMarkdown(bodyContent);
      nodes.push({
        type: 'admonition',
        attrs: { type, title, collapsible },
        content: childDoc.content && childDoc.content.length > 0
          ? childDoc.content
          : [{ type: 'paragraph', content: [] }],
      });
      continue;
    }

    // Mermaid code block
    if (line.trim() === '```mermaid') {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() !== '```') {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // consume closing ```
      nodes.push({
        type: 'mermaidBlock',
        attrs: { code: codeLines.join('\n') },
      });
      continue;
    }

    // Generic fenced code block
    const fenceMatch = line.match(/^```(\S*)?/);
    if (fenceMatch) {
      const lang = fenceMatch[1] || '';
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // consume closing ```
      nodes.push({
        type: 'codeBlock',
        attrs: { language: lang || null },
        content: [{ type: 'text', text: codeLines.join('\n') }],
      });
      continue;
    }

    // Heading
    const headingMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2].replace(/{[^}]*}$/, '').trim();
      nodes.push({
        type: 'heading',
        attrs: { level },
        content: parseInline(text),
      });
      i++;
      continue;
    }

    // Horizontal rule
    if (/^[-*_]{3,}\s*$/.test(line.trim())) {
      nodes.push({ type: 'horizontalRule' });
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        quoteLines.push(lines[i].slice(2));
        i++;
      }
      const innerDoc = fromMarkdown(quoteLines.join('\n'));
      nodes.push({
        type: 'blockquote',
        content: innerDoc.content || [{ type: 'paragraph' }],
      });
      continue;
    }

    // Unordered list
    if (/^[-*+] /.test(line)) {
      const listItems = parseList(lines, i, false);
      nodes.push({
        type: 'bulletList',
        content: listItems.items,
      });
      i = listItems.nextIndex;
      continue;
    }

    // Ordered list
    if (/^\d+\. /.test(line)) {
      const listItems = parseList(lines, i, true);
      nodes.push({
        type: 'orderedList',
        attrs: { start: 1 },
        content: listItems.items,
      });
      i = listItems.nextIndex;
      continue;
    }

    // Table
    if (line.includes('|') && i + 1 < lines.length && lines[i + 1].match(/^[|\s-:]+$/)) {
      const tableResult = parseTable(lines, i);
      nodes.push(tableResult.node);
      i = tableResult.nextIndex;
      continue;
    }

    // Paragraph (default)
    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !isBlockStart(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      const text = paraLines.join(' ');
      nodes.push({
        type: 'paragraph',
        content: parseInline(text),
      });
    }
  }

  if (nodes.length === 0) {
    nodes.push({ type: 'paragraph', content: [] });
  }

  return { type: 'doc', content: nodes };
}

function isBlockStart(line: string): boolean {
  return (
    /^#{1,6}\s/.test(line) ||
    /^[-*+] /.test(line) ||
    /^\d+\. /.test(line) ||
    line.startsWith('> ') ||
    line.startsWith('```') ||
    /^[!?]{3}\s/.test(line) ||
    line.includes('|')
  );
}

interface ListResult {
  items: JSONContent[];
  nextIndex: number;
}

function parseList(lines: string[], startIndex: number, ordered: boolean): ListResult {
  const items: JSONContent[] = [];
  let i = startIndex;
  const pattern = ordered ? /^\d+\. (.*)/ : /^[-*+] (.*)/;

  while (i < lines.length) {
    const match = lines[i].match(pattern);
    if (!match) break;

    const itemText = match[1];
    const subLines: string[] = [itemText];
    i++;

    // Collect continuation lines (indented)
    while (i < lines.length && (lines[i].startsWith('    ') || lines[i].startsWith('\t'))) {
      subLines.push(lines[i].replace(/^(\t|    )/, ''));
      i++;
    }

    items.push({
      type: 'listItem',
      content: [
        {
          type: 'paragraph',
          content: parseInline(subLines[0]),
        },
        ...(subLines.length > 1
          ? [fromMarkdown(subLines.slice(1).join('\n'))]
          : []),
      ],
    });
  }

  return { items, nextIndex: i };
}

interface TableResult {
  node: JSONContent;
  nextIndex: number;
}

function parseTable(lines: string[], startIndex: number): TableResult {
  let i = startIndex;
  const headerLine = lines[i];
  i++; // separator
  i++; // skip separator

  const headerCells = parseCells(headerLine);

  const rows: JSONContent[] = [
    {
      type: 'tableRow',
      content: headerCells.map((cell) => ({
        type: 'tableHeader',
        attrs: { colspan: 1, rowspan: 1, colwidth: null },
        content: [{ type: 'paragraph', content: parseInline(cell) }],
      })),
    },
  ];

  while (i < lines.length && lines[i].includes('|')) {
    const cells = parseCells(lines[i]);
    rows.push({
      type: 'tableRow',
      content: cells.map((cell) => ({
        type: 'tableCell',
        attrs: { colspan: 1, rowspan: 1, colwidth: null },
        content: [{ type: 'paragraph', content: parseInline(cell) }],
      })),
    });
    i++;
  }

  return {
    node: { type: 'table', content: rows },
    nextIndex: i,
  };
}

function parseCells(line: string): string[] {
  return line
    .split('|')
    .map((c) => c.trim())
    .filter((c, idx, arr) => idx > 0 && idx < arr.length - 1);
}

// Parse inline Markdown to TipTap inline content
function parseInline(text: string): JSONContent[] {
  const nodes: JSONContent[] = [];

  // Handle image with optional width attribute: ![alt](src){ width=400 }
  const imgRegex = /!\[([^\]]*)\]\(([^)]+)\)(?:\{([^}]*)\})?/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = imgRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const before = text.slice(lastIndex, match.index);
      nodes.push(...parseText(before));
    }

    const alt = match[1];
    const src = match[2];
    const attrsStr = match[3] || '';
    const widthMatch = attrsStr.match(/width\s*=\s*(\d+)/);
    const width = widthMatch ? parseInt(widthMatch[1], 10) : null;

    nodes.push({
      type: 'image',
      attrs: {
        src,
        alt,
        title: null,
        width,
      },
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(...parseText(text.slice(lastIndex)));
  }

  return nodes;
}

interface TextSegment {
  text: string;
  marks: { type: string; attrs?: Record<string, unknown> }[];
}

function parseText(text: string): JSONContent[] {
  const segments = splitByMarks(text);
  return segments.map((seg) => ({
    type: 'text',
    text: seg.text,
    marks: seg.marks.length > 0 ? seg.marks : undefined,
  }));
}

function splitByMarks(text: string): TextSegment[] {
  if (!text) return [];

  const result: TextSegment[] = [];

  // Process bold+italic, bold, italic, code, strikethrough, links
  const patterns: Array<{
    regex: RegExp;
    marks: (match: RegExpExecArray) => { type: string; attrs?: Record<string, unknown> }[];
    textGroup: number;
  }> = [
    {
      regex: /\*\*\*(.+?)\*\*\*/g,
      marks: () => [{ type: 'bold' }, { type: 'italic' }],
      textGroup: 1,
    },
    {
      regex: /\*\*(.+?)\*\*/g,
      marks: () => [{ type: 'bold' }],
      textGroup: 1,
    },
    {
      regex: /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g,
      marks: () => [{ type: 'italic' }],
      textGroup: 1,
    },
    {
      regex: /__(.+?)__/g,
      marks: () => [{ type: 'bold' }],
      textGroup: 1,
    },
    {
      regex: /_(.+?)_/g,
      marks: () => [{ type: 'italic' }],
      textGroup: 1,
    },
    {
      regex: /~~(.+?)~~/g,
      marks: () => [{ type: 'strike' }],
      textGroup: 1,
    },
    {
      regex: /`([^`]+)`/g,
      marks: () => [{ type: 'code' }],
      textGroup: 1,
    },
    {
      regex: /\[([^\]]+)\]\(([^)]+)\)/g,
      marks: (m) => [{ type: 'link', attrs: { href: m[2], target: '_blank' } }],
      textGroup: 1,
    },
    {
      regex: /\[\^(\d+)\]/g,
      marks: () => [{ type: 'superscript' }],
      textGroup: 0,
    },
  ];

  // Find first match across all patterns
  let remaining = text;
  let offset = 0;

  while (remaining.length > 0) {
    let firstMatch: RegExpExecArray | null = null;
    let firstPattern: (typeof patterns)[0] | null = null;
    let firstMatchStart = Infinity;

    for (const pattern of patterns) {
      pattern.regex.lastIndex = 0;
      const m = pattern.regex.exec(remaining);
      if (m && m.index < firstMatchStart) {
        firstMatch = m;
        firstPattern = pattern;
        firstMatchStart = m.index;
      }
    }

    if (!firstMatch || !firstPattern) {
      // No more marks — add remaining as plain text
      if (remaining) {
        result.push({ text: remaining, marks: [] });
      }
      break;
    }

    // Add text before match
    if (firstMatch.index > 0) {
      result.push({ text: remaining.slice(0, firstMatch.index), marks: [] });
    }

    // Add matched text with marks
    const innerText =
      firstPattern.textGroup === 0
        ? `[${firstMatch[1]}]`
        : firstMatch[firstPattern.textGroup];

    result.push({
      text: innerText,
      marks: firstPattern.marks(firstMatch),
    });

    remaining = remaining.slice(firstMatch.index + firstMatch[0].length);
    offset += firstMatch.index + firstMatch[0].length;
  }

  return result;
}
